import { EventEmitter, Injectable } from '@angular/core';

export const cssContentPadding: number = 10;

export const cssWhite = '#fff';
export const cssGray100 = '#f8f9fa';
export const cssGray200 = '#e9ecef';
export const cssGray300 = '#dee2e6';
export const cssGray400 = '#ced4da';
export const cssGray500 = '#adb5bd';
export const cssGray600 = '#6c757d';
export const cssGray700 = '#495057';
export const cssGray800 = '#343a40';
export const cssGray900 = '#212529';
export const cssBlack = '#000';

export const cssBlue = '#007bff';
export const cssIndigo = '#6610f2';
export const cssPurple = '#6f42c1';
export const cssPink = '#e83e8c';
export const cssRed = '#dc3545';
export const cssOrange = '#fd7e14';
export const cssYellow = '#ffc107';
export const cssGreen = '#28a745';
export const cssTeal = '#20c997';
export const cssCyan = '#17a2b8';
export const cssPrimary = '#2191a1';
export const cssSecondary = cssGray600;
export const cssSuccess = cssGreen;
export const cssInfo = '#23b7e5';
export const cssWarning = '#ff902b';
export const cssDanger = cssRed;
export const cssLight = cssGray100;
export const cssDark = cssGray800;

@Injectable({
  providedIn: 'root'
})
export class LayoutService {

  constructor() {}

  private _title: string = '';
  private _sidebar: boolean = false;
  private _menu: MenuItem[];
  private _media: { [name: string]: 'all' | 'print' | 'screen' | 'none' } = {};


  private static _resize = new EventEmitter<{ width: number, height: number, type: 'layout' | 'content' | 'sidebar', scope: HTMLElement }>();
  private _sizeProvider: LayoutSizeProvider;

  private static _change = new EventEmitter<{ type: 'sidebar' | 'menu' | 'title' | 'scroll' | 'content', scope: HTMLElement }>();

  public static get resize() {
    return LayoutService._resize;
  }

  public get resize() {
    return LayoutService._resize;
  }

  public static get change() {
    return LayoutService._change;
  }

  public get change() {
    return LayoutService._change;
  }

  public get title(): string {
    return this._title;
  }

  public set title(value: string) {
    if (this._title != value) {
      this._title = value;
      LayoutService._change.emit({ type: 'title', scope: document.body });
    }
  }

  public get sidebar(): boolean {
    return this._sidebar;
  }

  public get fullscreen() {
    return document.fullscreen;
  }

  public set sidebar(value: boolean) {
    if (this._sidebar != value) {
      this._sidebar = value;
      LayoutService._change.emit({ type: 'sidebar', scope: document.body });
    }
  }

  public media(component: 'header' | 'footer', media?: 'print' | 'screen' | 'all' | 'none'): 'print' | 'screen' | 'all' | 'none' {

    let key = [component, window.location.pathname].join(':');
    if (media) this._media[key] = media;

    return this._media[key] ? this._media[key] : 'all';
  }

  public get menu(): MenuItem[] {
    return this._menu;
  }

  public set menu(items: MenuItem[]) {
    this._menu = items;
    LayoutService._change.emit({ type: 'menu', scope: document.body });
  }

  public use(provider: LayoutSizeProvider) {
    this._sizeProvider = provider;
  }

  public bounds(type: 'layout' | 'content' | 'sidebar' | 'header' | 'footer'): DOMRect | ClientRect {

    if (!this._sizeProvider) return { x: 0, y: 0, width: 0, height: 0, top: 0, left: 0, bottom: 0, right: 0 };

    switch (type) {
      case 'layout': return document.body.getBoundingClientRect();
      case 'header': return this._sizeProvider.header();
      case 'sidebar': return this._sizeProvider.sidebar();
      case 'content': return this._sizeProvider.content();
      case 'footer': return this._sizeProvider.footer();
      default: return { x: 0, y: 0, width: 0, height: 0, top: 0, left: 0, bottom: 0, right: 0 };
    }
  }
}

export interface MenuItem {

  text?: string;
  heading?: boolean;
  link?: string;
  icon?: string;
  condition?(): boolean;
  action?(): void;
}

export interface LayoutSizeProvider {
  header: () => DOMRect | ClientRect;
  sidebar: () => DOMRect | ClientRect;
  content: () => DOMRect | ClientRect;
  footer: () => DOMRect | ClientRect;
}
